(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compactViewport = window.matchMedia('(max-width: 749px)');
  const mounted = new WeakSet();
  const instances = new Set();

  const regions = [
    { selector: '.sento-lunar-hero', density: 'hero', mode: 'moissanite' },
    {
      selector: '.page-type-collection.resource-handle-lunar-goddess-moissanite-jewelry .collection-hero',
      density: 'banner',
      mode: 'moissanite',
    },
    { selector: '.resource-handle-about .sento-page-shell--about', density: 'about', mode: 'ambient' },
    { selector: '.page-type-404 .template-404', density: 'hero', mode: 'ambient' },
    { selector: '.email-signup-banner', density: 'cta', mode: 'ambient' },
    { selector: '.shopify-section > .newsletter', density: 'cta', mode: 'ambient' },
  ];

  const densityLimits = {
    hero: { desktop: [72, 112], mobile: [30, 44] },
    banner: { desktop: [54, 86], mobile: [26, 38] },
    about: { desktop: [44, 68], mobile: [24, 34] },
    cta: { desktop: [40, 64], mobile: [22, 32] },
  };

  const moissaniteDensityLimits = {
    hero: { desktop: [82, 118], mobile: [34, 46] },
    banner: { desktop: [64, 94], mobile: [30, 42] },
  };

  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));

  function seedFrom(value) {
    let seed = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      seed ^= value.charCodeAt(index);
      seed = Math.imul(seed, 16777619);
    }
    return seed >>> 0;
  }

  function randomFactory(seedValue) {
    let seed = seedValue || 1;
    return () => {
      seed += 0x6d2b79f5;
      let value = seed;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  }

  class Starfield {
    constructor(element, density, mode, index) {
      this.element = element;
      this.density = density;
      this.mode = mode;
      this.isMoissaniteHero = mode === 'moissanite';
      this.random = randomFactory(seedFrom(`${density}:${mode}:${index}:${element.id || element.className}`));
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'sento-starfield-canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.canvas.dataset.sentoStarfieldMode = mode;
      this.context = this.canvas.getContext('2d', { alpha: true, desynchronized: true });
      this.stars = [];
      this.canvas.dataset.sentoLayers = 'stars-meteors';
      this.canvas.dataset.sentoPointerInteraction = 'none';
      this.visible = typeof IntersectionObserver !== 'function';
      this.focused = document.hasFocus();
      this.frame = 0;
      this.lastFrame = 0;
      this.meteor = null;
      this.nextMeteorAt = 0;
      this.meteorCount = 0;
      this.followWithSecondMeteor = false;
      this.createdAt = performance.now();
      this.pausedAt = (!this.visible || !this.focused) ? performance.now() : 0;

      if (!this.context) {
        mounted.delete(this.element);
        return;
      }

      this.element.classList.add('sento-starfield-region');
      this.element.prepend(this.canvas);
      this.render = this.render.bind(this);
      this.resize = this.resize.bind(this);
      this.resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(this.resize) : null;
      if (this.resizeObserver) this.resizeObserver.observe(this.element);
      else window.addEventListener('resize', this.resize, { passive: true });

      this.intersectionObserver = typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
          this.visible = Boolean(entries[0]?.isIntersecting);
          if (this.visible) this.resume();
          else this.pause();
        }, { rootMargin: '0px', threshold: 0.01 })
        : null;
      this.intersectionObserver?.observe(this.element);

      instances.add(this);
      this.resize();
    }

    pause() {
      if (!this.pausedAt) this.pausedAt = performance.now();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    resume() {
      if (!reducedMotion.matches && this.visible && this.focused && !document.hidden && !this.frame) {
        const now = performance.now();
        if (this.pausedAt) {
          const pausedFor = Math.max(0, now - this.pausedAt);
          if (Number.isFinite(this.nextMeteorAt)) this.nextMeteorAt += pausedFor;
          if (this.meteor) this.meteor.startedAt += pausedFor;
          this.createdAt += pausedFor;
          this.pausedAt = 0;
        }
        this.lastFrame = now;
        this.frame = requestAnimationFrame(this.render);
      }
    }

    destroy() {
      this.pause();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (!this.resizeObserver) window.removeEventListener('resize', this.resize);
      this.canvas.remove();
      this.element.classList.remove('sento-starfield-region');
      mounted.delete(this.element);
      instances.delete(this);
    }

    resize() {
      const bounds = this.element.getBoundingClientRect();
      this.width = Math.max(1, Math.round(bounds.width));
      const aboutHeight = this.density === 'about'
        ? Math.min(bounds.height, compactViewport.matches ? 420 : 620)
        : bounds.height;
      this.height = Math.max(1, Math.round(aboutHeight));
      this.pixelRatio = compactViewport.matches ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.round(this.width * this.pixelRatio);
      this.canvas.height = Math.round(this.height * this.pixelRatio);
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
      this.context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);

      const limits = this.isMoissaniteHero
        ? moissaniteDensityLimits[this.density]
        : densityLimits[this.density] || densityLimits.banner;
      const [minimum, maximum] = compactViewport.matches ? limits.mobile : limits.desktop;
      const areaCount = Math.round((this.width * this.height) / (compactViewport.matches ? 10200 : 7900));
      const count = Math.max(minimum, Math.min(maximum, areaCount));
      this.buildStars(count);

      if (!this.nextMeteorAt) {
        const firstDelay = this.meteorDelay(true);
        this.nextMeteorAt = this.createdAt + firstDelay;
        this.canvas.dataset.sentoFirstMeteorTargetMs = String(Math.round(firstDelay));
      }
      this.draw(performance.now(), reducedMotion.matches);
      this.resume();
    }

    buildStars(count) {
      if (!this.isMoissaniteHero) {
        this.stars = Array.from({ length: count }, () => {
          const tierRoll = this.random();
          const tier = tierRoll > 0.985 ? 2 : tierRoll > 0.89 ? 1 : 0;
          return {
            x: this.random(),
            y: this.random(),
            radius: tier === 2 ? 1.45 + this.random() * 0.55 : tier === 1 ? 0.8 + this.random() * 0.45 : 0.35 + this.random() * 0.45,
            alpha: tier === 2 ? 0.62 + this.random() * 0.22 : tier === 1 ? 0.34 + this.random() * 0.2 : 0.13 + this.random() * 0.18,
            phase: this.random() * Math.PI * 2,
            speed: 0.00035 + this.random() * 0.00055,
            tier,
            cool: this.random() > 0.26,
          };
        });
        this.canvas.dataset.sentoStarCount = String(count);
        return;
      }
      this.stars = Array.from({ length: count }, () => {
        const tierRoll = this.random();
        const tier = tierRoll > 0.965 ? 2 : tierRoll > 0.72 ? 1 : 0;
        return {
          x: this.random(),
          y: this.random(),
          radius: tier === 2
            ? 1.25 + this.random() * 0.65
            : tier === 1
              ? 0.68 + this.random() * 0.48
              : 0.3 + this.random() * 0.42,
          alpha: tier === 2
            ? 0.58 + this.random() * 0.2
            : tier === 1
              ? 0.28 + this.random() * 0.2
              : 0.1 + this.random() * 0.16,
          phase: this.random() * Math.PI * 2,
          speed: tier === 0 ? 0 : 0.00022 + this.random() * 0.0005,
          sparkPhase: this.random() * Math.PI * 2,
          sparkSpeed: 0.00018 + this.random() * 0.0003,
          tier,
          tint: Math.floor(this.random() * 4),
          flareAt: performance.now() + 2200 + this.random() * 6500,
          flareStartedAt: 0,
          flareUntil: 0,
        };
      });
      if (!this.stars.some((star) => star.tier === 2)) {
        const highlight = this.stars[Math.floor(this.random() * this.stars.length)];
        highlight.tier = 2;
        highlight.radius = 1.55;
        highlight.alpha = 0.68;
        highlight.speed = 0.00034;
      }
      this.canvas.dataset.sentoStarCount = String(count);
    }

    meteorDelay(first = false) {
      if (this.isMoissaniteHero) {
        if (first) return compactViewport.matches
          ? 4000 + this.random() * 4000
          : 2500 + this.random() * 2500;
        return compactViewport.matches
          ? 16000 + this.random() * 12000
          : 9000 + this.random() * 9000;
      }
      return compactViewport.matches
        ? 48000 + this.random() * 32000
        : 24000 + this.random() * 22000;
    }

    startMeteor(now) {
      if (this.isMoissaniteHero) {
        const direction = this.random() > 0.5 ? 1 : -1;
        const travel = this.width * (0.25 + this.random() * 0.3);
        const startX = direction > 0
          ? this.width * (0.06 + this.random() * 0.18)
          : this.width * (0.76 + this.random() * 0.18);
        const startY = this.height * (0.1 + this.random() * 0.22);
        const desiredY = travel * (0.28 + this.random() * 0.16);
        const deltaY = Math.min(desiredY, Math.max(travel * 0.22, this.height * 0.78 - startY));
        const mobile = compactViewport.matches;
        const tail = clamp(
          this.width * (0.15 + this.random() * 0.07),
          140,
          mobile ? 190 : 300,
        );
        this.meteor = {
          startX,
          startY,
          deltaX: travel * direction,
          deltaY,
          tail,
          startedAt: now,
          duration: 700 + this.random() * 700,
          tint: this.random(),
        };
        this.followWithSecondMeteor = this.random() < 0.065;
      } else {
        const travel = Math.min(150, Math.max(88, this.width * 0.12));
        this.meteor = {
          startX: this.width * (0.56 + this.random() * 0.34),
          startY: this.height * (0.08 + this.random() * 0.22),
          deltaX: -travel,
          deltaY: travel * 0.34,
          tail: 34 + this.random() * 26,
          startedAt: now,
          duration: 980 + this.random() * 420,
          tint: 0,
        };
        this.followWithSecondMeteor = false;
      }

      this.meteorCount += 1;
      this.canvas.dataset.sentoMeteorCount = String(this.meteorCount);
      this.canvas.dataset.sentoLastMeteorStartMs = String(Math.round(now - this.createdAt));
      if (this.meteorCount === 1) {
        this.canvas.dataset.sentoFirstMeteorActualMs = String(Math.round(now - this.createdAt));
      }
      this.nextMeteorAt = Number.POSITIVE_INFINITY;
    }

    finishMeteor(now) {
      this.meteor = null;
      if (this.followWithSecondMeteor) {
        this.followWithSecondMeteor = false;
        this.nextMeteorAt = now + 1800 + this.random() * 1600;
      } else {
        this.nextMeteorAt = now + this.meteorDelay(false);
      }
    }

    drawMoissaniteStar(star, now, staticFrame) {
      const x = star.x * this.width;
      const y = star.y * this.height;
      const twinkle = !staticFrame && star.tier > 0
        ? Math.sin(now * star.speed + star.phase) * (star.tier === 2 ? 0.11 : 0.055)
        : 0;
      const spark = !staticFrame && star.tier > 0
        ? Math.pow(Math.max(0, Math.sin(now * star.sparkSpeed + star.sparkPhase)), star.tier === 2 ? 8 : 18)
        : 0;
      let flarePulse = 0;
      if (!staticFrame && star.tier === 2) {
        if (now >= star.flareAt && now >= star.flareUntil) {
          star.flareStartedAt = now;
          star.flareUntil = now + 180 + this.random() * 140;
          star.flareAt = star.flareUntil + 4000 + this.random() * 8000;
        }
        if (now < star.flareUntil) {
          const flareProgress = (now - star.flareStartedAt) / (star.flareUntil - star.flareStartedAt);
          flarePulse = Math.sin(Math.PI * clamp(flareProgress, 0, 1));
        }
      }
      const alpha = clamp(star.alpha + twinkle + spark * 0.08, 0.04, 0.94);
      const radius = star.radius * (1 + spark * 0.1);
      const colors = [[226, 235, 249], [211, 227, 251], [231, 222, 245], [238, 235, 224]];
      const [red, green, blue] = colors[star.tint];

      this.context.save();
      if (star.tier === 2 && flarePulse > 0) {
        this.context.shadowColor = `rgba(${red}, ${green}, ${blue}, ${alpha * 0.48})`;
        this.context.shadowBlur = 5 + flarePulse * 5;
      }
      this.context.beginPath();
      this.context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha})`;
      this.context.arc(x, y, radius, 0, Math.PI * 2);
      this.context.fill();
      this.context.restore();

      if (star.tier === 2 && flarePulse > 0) {
        const flare = 4.2 + flarePulse * 4.8;
        this.context.save();
        this.context.strokeStyle = `rgba(${red}, ${green}, ${blue}, ${alpha * (0.22 + flarePulse * 0.42)})`;
        this.context.lineWidth = 0.55;
        this.context.beginPath();
        this.context.moveTo(x - flare, y);
        this.context.lineTo(x + flare, y);
        this.context.moveTo(x, y - flare * 0.78);
        this.context.lineTo(x, y + flare * 0.78);
        this.context.stroke();
        this.context.restore();
      }
    }

    drawAmbientStar(star, now, staticFrame) {
      const x = star.x * this.width;
      const y = star.y * this.height;
      const twinkle = !staticFrame && star.tier > 0
        ? Math.sin(now * star.speed + star.phase) * (star.tier === 2 ? 0.14 : 0.08)
        : 0;
      const alpha = Math.min(0.9, star.alpha + twinkle);
      this.context.beginPath();
      this.context.fillStyle = star.cool
        ? `rgba(218, 230, 249, ${alpha})`
        : `rgba(233, 222, 244, ${alpha * 0.78})`;
      this.context.arc(x, y, star.radius, 0, Math.PI * 2);
      this.context.fill();
      if (star.tier === 2) {
        this.context.strokeStyle = `rgba(226, 235, 250, ${alpha * 0.34})`;
        this.context.lineWidth = 0.55;
        this.context.beginPath();
        this.context.moveTo(x - 4.2, y);
        this.context.lineTo(x + 4.2, y);
        this.context.moveTo(x, y - 4.2);
        this.context.lineTo(x, y + 4.2);
        this.context.stroke();
      }
    }

    drawMeteor(now) {
      if (!this.meteor) return;
      const progress = (now - this.meteor.startedAt) / this.meteor.duration;
      if (progress >= 1) {
        this.finishMeteor(now);
        return;
      }
      if (!this.isMoissaniteHero) {
        const eased = progress * (2 - progress);
        const x = this.meteor.startX + this.meteor.deltaX * eased;
        const y = this.meteor.startY + this.meteor.deltaY * eased;
        const opacity = Math.sin(Math.PI * progress) * 0.22;
        const gradient = this.context.createLinearGradient(x, y, x + this.meteor.tail, y - this.meteor.tail * 0.34);
        gradient.addColorStop(0, 'rgba(226, 235, 250, 0)');
        gradient.addColorStop(0.82, 'rgba(226, 235, 250, ' + (opacity * 0.45) + ')');
        gradient.addColorStop(1, 'rgba(244, 247, 252, ' + opacity + ')');
        this.context.strokeStyle = gradient;
        this.context.lineWidth = 0.8;
        this.context.beginPath();
        this.context.moveTo(x, y);
        this.context.lineTo(x + this.meteor.tail, y - this.meteor.tail * 0.34);
        this.context.stroke();
        return;
      }
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const x = this.meteor.startX + this.meteor.deltaX * eased;
      const y = this.meteor.startY + this.meteor.deltaY * eased;
      const travelLength = Math.hypot(this.meteor.deltaX, this.meteor.deltaY) || 1;
      const unitX = this.meteor.deltaX / travelLength;
      const unitY = this.meteor.deltaY / travelLength;
      const tailX = x - unitX * this.meteor.tail;
      const tailY = y - unitY * this.meteor.tail;
      const fade = Math.sin(Math.PI * progress);
      const peak = this.isMoissaniteHero ? 0.68 : 0.22;
      const opacity = fade * peak;
      const gradient = this.context.createLinearGradient(tailX, tailY, x, y);
      gradient.addColorStop(0, 'rgba(155, 180, 220, 0)');
      gradient.addColorStop(0.56, `rgba(174, 202, 239, ${opacity * 0.2})`);
      gradient.addColorStop(0.88, `rgba(222, 235, 252, ${opacity * 0.68})`);
      gradient.addColorStop(1, `rgba(250, 252, 255, ${opacity})`);

      this.context.save();
      this.context.globalCompositeOperation = 'screen';
      this.context.strokeStyle = gradient;
      this.context.lineWidth = this.isMoissaniteHero ? 1.45 : 0.8;
      this.context.shadowColor = `rgba(207, 225, 251, ${opacity * 0.74})`;
      this.context.shadowBlur = this.isMoissaniteHero ? 11 : 4;
      this.context.beginPath();
      this.context.moveTo(tailX, tailY);
      this.context.lineTo(x, y);
      this.context.stroke();

      if (this.isMoissaniteHero) {
        const accent = this.meteor.tint > 0.66
          ? `rgba(231, 218, 245, ${opacity * 0.26})`
          : `rgba(184, 215, 250, ${opacity * 0.28})`;
        this.context.shadowBlur = 0;
        this.context.strokeStyle = accent;
        this.context.lineWidth = 0.55;
        this.context.beginPath();
        this.context.moveTo(tailX + unitY * 1.3, tailY - unitX * 1.3);
        this.context.lineTo(x + unitY * 1.3, y - unitX * 1.3);
        this.context.stroke();
        this.context.fillStyle = `rgba(252, 253, 255, ${opacity})`;
        this.context.beginPath();
        this.context.arc(x, y, 1.35 + fade * 0.7, 0, Math.PI * 2);
        this.context.fill();
      }
      this.context.restore();
    }

    draw(now, staticFrame = false) {
      this.context.clearRect(0, 0, this.width, this.height);
      for (const star of this.stars) {
        if (this.isMoissaniteHero) this.drawMoissaniteStar(star, now, staticFrame);
        else this.drawAmbientStar(star, now, staticFrame);
      }
      if (!staticFrame) this.drawMeteor(now);
    }

    render(now) {
      this.frame = 0;
      if (!this.visible || !this.focused || reducedMotion.matches || document.hidden) return;
      const frameInterval = compactViewport.matches ? 42 : this.isMoissaniteHero ? 22 : 33;
      if (now - this.lastFrame >= frameInterval) {
        this.lastFrame = now;
        if (!this.meteor && now >= this.nextMeteorAt) this.startMeteor(now);
        this.draw(now, false);
      }
      this.frame = requestAnimationFrame(this.render);
    }
  }

  function mount() {
    let index = 0;
    for (const region of regions) {
      for (const element of document.querySelectorAll(region.selector)) {
        if (mounted.has(element)) continue;
        mounted.add(element);
        new Starfield(element, region.density, region.mode, index++);
      }
    }
  }

  const onMotionPreferenceChange = () => {
    for (const instance of instances) {
      instance.pause();
      instance.meteor = null;
      if (reducedMotion.matches) instance.draw(performance.now(), true);
      else {
        const now = performance.now();
        if (instance.pausedAt) {
          instance.createdAt += Math.max(0, now - instance.pausedAt);
          instance.pausedAt = 0;
        }
        instance.nextMeteorAt = now + instance.meteorDelay(instance.meteorCount === 0);
        instance.resume();
      }
    }
  };

  if (typeof reducedMotion.addEventListener === 'function') reducedMotion.addEventListener('change', onMotionPreferenceChange);
  else if (typeof reducedMotion.addListener === 'function') reducedMotion.addListener(onMotionPreferenceChange);

  document.addEventListener('visibilitychange', () => {
    for (const instance of instances) {
      if (document.hidden) instance.pause();
      else instance.resume();
    }
  });
  window.addEventListener('blur', () => {
    for (const instance of instances) {
      instance.focused = false;
      instance.pause();
    }
  });
  window.addEventListener('focus', () => {
    for (const instance of instances) {
      instance.focused = true;
      instance.resume();
    }
  });
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const instance of [...instances]) {
      if (event.target === instance.element || event.target.contains(instance.element)) instance.destroy();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
